import { createElement } from 'react'
import { FileText } from 'lucide-react'
import { HttpError, type AgentFrontendConfig, type ToolRendererRegistry } from '@multiplayer-app/ai-agent-react'
import type { OfficeHost } from '@/shared/office/host'
import type { ColorMode } from '@/shared/office/theme'
import { officeToolsForHost } from './tools'
import { buildContextKeys } from './contextKeys'

import { aiApiBaseUrl, aiAgentSocketPath } from '@/shared/config/endpoints'

const DEFAULT_CONTEXT_KEY = import.meta.env.VITE_DEFAULT_CONTEXT_KEY || 'office'

/** Theme palettes matching the Office host theme. */
const PALETTES: Record<ColorMode, Record<string, string>> = {
  light: {
    accentSoft: '#e8eef8',
    background: '#ffffff',
    surface: '#f7f8fa',
    text: '#1a202c',
    subtext: '#718096',
    border: '#e2e8f0'
  },
  dark: {
    accentSoft: '#1a3058',
    background: '#1f1f23',
    surface: '#2a2a2e',
    text: '#f0f2f5',
    subtext: '#a0aec0',
    border: '#3d444d'
  }
}

/**
 * Auth failures from the backend are not always 401: the auth microservice
 * reports unknown/revoked tokens as 404 "The token with accessToken: ... is
 * not found." Plain 404s (e.g. a deleted chat) must NOT trigger sign-out.
 */
function isAuthError(error: unknown): boolean {
  if (!(error instanceof HttpError)) return false
  if (error.statusCode === 401) return true
  return error.statusCode === 404 && /token/i.test(error.message) && /not found/i.test(error.message)
}

export interface BuildAgentConfigOptions {
  onUnauthorized?: () => void
  colorMode?: ColorMode
  /** Chat tool-call renderers, injected by the caller (owned by the chat screen). */
  toolRenderers?: ToolRendererRegistry
}

export function buildAgentConfig(
  host: OfficeHost,
  token: string,
  { onUnauthorized, colorMode = 'light', toolRenderers }: BuildAgentConfigOptions = {}
): AgentFrontendConfig {
  const officeTools = officeToolsForHost(host)
  const palette = PALETTES[colorMode]

  return {
    appId: 'ramp-office-addin',
    user: {
      id: window.localStorage.getItem('userId') || 'ramp-office-user'
      // displayName: `Ramp ${HOST_LABELS[host]} User`
    },
    transport: {
      mode: 'proxy',
      baseUrl: aiApiBaseUrl,
      socketPath: aiAgentSocketPath,
      timeoutMs: 5 * 60 * 1000,
      headers: { Authorization: token },
      // The stored token can be revoked/expired server-side before its local
      // expiry; sign the user out so they land on the login screen instead of
      // every request silently failing.
      onError: (error) => {
        if (isAuthError(error)) onUnauthorized?.()
      }
    },
    debug: false,
    defaultContextKey: DEFAULT_CONTEXT_KEY,
    contextKeys: buildContextKeys(DEFAULT_CONTEXT_KEY, officeTools),
    tools: officeTools,
    toolRenderers,
    models: [],
    features: {
      reasoning: true,
      fileAttachments: false,
      modelSwitching: true,
      titleGeneration: 'llm',
      // Replaced by our own document-scoped history drawer (DocumentChatHistory):
      // the built-in sidebar lists every chat with no way to filter by document.
      historySidebar: false
    },
    pageContext: {
      includeUserAgent: false,
      includeReferrer: false,
      includeViewport: false,
      includeTitle: false,
      name: 'Document context'
    },
    // The ambient context is the open document, not a web page — show it as one.
    contextAttachmentIcons: { pageContext: createElement(FileText, { size: 14 }) },
    theme: {
      colorMode,
      accent: '#1547a3',
      ...palette,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      radius: '12px',
      fontSize: '14px'
    }
  }
}
